import { useState } from 'react';

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'Do I really not have to clean anything before you buy my house?',
      answer: 'Absolutely not. We buy your property exactly as it is, with all contents included. You don\'t have to remove a single item, clean anything, or make any preparations. Our team handles 100% of the cleanup after we purchase the property. This is one of the main benefits of selling to us - you avoid the overwhelming task of cleaning out a hoarder house.'
    },
    {
      question: 'How do you determine the offer price for a hoarder house?',
      answer: 'We base our offer on the property\'s location, size, condition, and current market value - not on the amount of clutter or cleanup required. We factor in our cleanup costs, but you still receive a fair market offer. We\'re transparent about how we calculate our offers and happy to explain our reasoning. Many sellers are pleasantly surprised by our offers because we look at the property\'s potential, not just its current state.'
    },
    {
      question: 'What happens to all the items in the house?',
      answer: 'After we purchase the property, our team carefully sorts through all items. We identify anything of value (jewelry, collectibles, important documents) and set it aside for your review. Usable items in good condition are donated to local charities. Recyclable materials are properly recycled. The remaining items are disposed of responsibly and legally. We handle everything according to local regulations and environmental guidelines.'
    },
    {
      question: 'How long does the entire process take?',
      answer: 'From your first contact to closing can be as quick as 7-14 days if you need to sell fast. However, we can also work on your timeline if you need more time. The typical process includes: initial contact (day 1), property walkthrough (days 2-3), cash offer presented (days 3-4), offer acceptance and closing scheduled (day 5), and closing/payment (days 7-14). After closing, we handle all cleanup - you don\'t need to be involved.'
    },
    {
      question: 'Will my neighbors know about the hoarding situation?',
      answer: 'We handle every transaction with complete discretion and respect for your privacy. Our team works professionally and doesn\'t draw unnecessary attention. We don\'t put signs in the yard, and we can schedule our walkthrough at times that work best for your privacy. The cleanup process after closing is handled efficiently and professionally. Your situation remains confidential.'
    },
    {
      question: 'What if the house has structural damage or safety issues?',
      answer: 'We buy houses in any condition, including those with structural damage, safety hazards, or code violations. Our team has experience assessing and repairing all types of property issues. Structural damage, compromised floors, damaged walls, or other safety concerns don\'t prevent us from making an offer. We factor these issues into our offer price but still provide a fair market value for your property.'
    },
    {
      question: 'Can I keep some items before you buy the house?',
      answer: 'Absolutely! Before closing, you\'re welcome to remove any items you want to keep. We can also help identify potentially valuable items during our walkthrough. If you\'re unsure about certain items, we can set them aside after purchase for you to review before we dispose of anything. We understand that some items may have sentimental or monetary value, and we respect that.'
    },
    {
      question: 'Do you buy hoarder houses in any location?',
      answer: 'We purchase hoarder houses throughout our service area. Contact us with your property location, and we\'ll let you know if we can help. Even if your property is outside our primary area, we may still be able to assist or connect you with a trusted partner who can help. Distance is rarely a barrier when it comes to helping someone with a hoarder house situation.'
    },
    {
      question: 'What if there are pests or biohazards in the property?',
      answer: 'Pest infestations, mold, biohazards, and other health concerns are common in hoarder houses, and we\'re fully prepared to handle them. Our team follows proper safety protocols and works with licensed professionals for specialized cleanup. These issues don\'t prevent us from buying your property - they\'re part of what we specialize in handling. Your safety is important, so we don\'t require you to address these issues before selling.'
    },
    {
      question: 'Is your offer really no-obligation?',
      answer: 'Yes, completely no-obligation. After we view your property, we\'ll present you with a fair cash offer. You can accept it, decline it, or take time to think about it - there\'s absolutely no pressure. We understand this is a big decision, and we want you to feel comfortable with whatever choice you make. Even if you decide not to sell to us, we\'re happy to have helped you understand your options.'
    },
    {
      question: 'How do you handle inherited hoarder houses?',
      answer: 'Inherited hoarder houses are one of our specialties. We understand the emotional complexity of dealing with a deceased loved one\'s property, especially when hoarding was involved. We can work with multiple heirs, handle probate situations, and provide the time you need to process the situation. Our compassionate approach helps families navigate this difficult time without the added stress of cleanup and traditional selling.'
    },
    {
      question: 'What makes you different from other house buyers?',
      answer: 'We specialize specifically in challenging properties like hoarder houses. While other buyers may claim to buy "as-is," they often back out when they see the actual condition. We have extensive experience with hoarder properties, established cleanup systems, and the resources to handle even the most overwhelming situations. Most importantly, we approach every situation with genuine compassion and respect - this isn\'t just business to us, it\'s about helping people through difficult circumstances.'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked <span className="text-teal-600">Questions</span>
          </h2>
          <p className="text-xl text-gray-600">
            Get answers to common questions about selling your hoarder house to us.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-gray-50 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-100 transition-colors duration-200"
              >
                <span className="text-lg font-bold text-gray-900 pr-8">{faq.question}</span>
                <i className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-2xl text-teal-600 flex-shrink-0`}></i>
              </button>
              
              {openIndex === index && (
                <div className="px-8 pb-6">
                  <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-lg text-gray-700 mb-6">
            Still have questions? We're here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+1234567890"
              className="px-8 py-4 bg-teal-600 text-white text-lg font-semibold rounded-lg hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call Us Now
            </a>
            <button 
              onClick={() => {
                const form = document.getElementById('contact-form');
                if (form) form.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-8 py-4 bg-gray-900 text-white text-lg font-semibold rounded-lg hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Send Us a Message
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
